import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createIntl, createIntlCache, RawIntlProvider, ReactIntlErrorCode } from 'react-intl';
import { decorateIntl } from './intlDecorate';
import { CORE_NAMESPACES, loadMessages, namespacesFor, sourceCore, type Catalog, type CompleteCatalog } from './catalog';
import { setRuntimeIntl } from './runtime';
import { defineMessages } from './define';
import { I18nContext, type I18nValue } from './context';
import { SOURCE_LOCALE, enabledLocales, initialLocale, storeLocale, type Locale } from './locales';

/** То, с чем приложение стартует: язык и его ядро, загруженные до первого кадра. */
export interface I18nBoot {
  locale: Locale;
  messages: Catalog;
}

/**
 * Загрузить ядро языка ДО первого кадра: узбекоязычный человек не должен на
 * каждой загрузке видеть вспышку русского интерфейса. Для языка-источника
 * ответ готов синхронно (ядро вшито), сеть не нужна.
 */
export async function prepareI18n(locale: Locale = initialLocale()): Promise<I18nBoot> {
  try {
    return { locale, messages: await loadMessages(locale, namespacesFor(locale)) };
  } catch {
    // Чанк каталога не догрузился (сеть, выкладка новой сборки): интерфейс
    // открывается на языке-источнике, а не остаётся белым экраном.
    return { locale: SOURCE_LOCALE, messages: sourceCore() };
  }
}

const DOC = defineMessages({
  title: { id: 'shell.document.title', defaultMessage: 'Agro AI — аналитическая станция района', description: 'Заголовок вкладки браузера' },
});

function onIntlError(error: { code: string; message: string }): void {
  // Пропуск перевода — штатное состояние (откат к источнику уже сделан
  // слиянием каталогов); охват считает `npm run i18n:check`, а не консоль.
  if (error.code === ReactIntlErrorCode.MISSING_TRANSLATION) return;
  if (import.meta.env.DEV) console.error('[i18n]', error.message);
}

interface Active {
  locale: Locale;
  messages: Catalog;
  namespaces: ReadonlySet<string>;
}

export function I18nProvider({
  children,
  boot,
  locales,
}: {
  children: ReactNode;
  boot?: I18nBoot;
  /** Языки переключателя; по умолчанию — `enabledLocales()`. */
  locales?: readonly Locale[];
}) {
  const enabled = useMemo(() => locales ?? enabledLocales(), [locales]);
  const [active, setActive] = useState<Active>(() => ({
    locale: boot?.locale ?? SOURCE_LOCALE,
    messages: boot?.messages ?? sourceCore(),
    namespaces: new Set(boot ? namespacesFor(boot.locale) : CORE_NAMESPACES),
  }));
  const target = useRef<Locale>(active.locale);
  const wanted = useRef(new Set<string>(active.namespaces));
  const ticket = useRef(0);

  /**
   * Загрузить каталоги целевого языка и переключиться разом. Пока грузится,
   * на экране прежний язык целиком — а не половина строк на новом. Ответ на
   * устаревший запрос (человек успел выбрать третий язык) отбрасывается.
   */
  const reload = useCallback(() => {
    const mine = ++ticket.current;
    const locale = target.current;
    const namespaces = [...new Set([...wanted.current, ...namespacesFor(locale)])];
    loadMessages(locale, namespaces).then(
      (messages) => {
        if (mine !== ticket.current) return;
        setActive({ locale, messages, namespaces: new Set(namespaces) });
      },
      (error: unknown) => {
        // Каталог не загрузился — остаёмся на показанном языке целиком.
        if (import.meta.env.DEV) console.error('[i18n] каталог не загружен:', error);
      },
    );
  }, []);

  const setLocale = useCallback(
    (locale: Locale) => {
      if (!enabled.includes(locale)) return;
      storeLocale(locale, { explicit: true });
      // В браузере — перезагрузка страницы: подписи, которые адаптеры данных
      // собрали и запомнили вместе с ответом сервера (кэш запросов), иначе
      // остались бы на прежнем языке. Каталог нового языка грузится до
      // первого кадра (`prepareI18n`), вспышки прежнего языка нет. В тестах
      // (jsdom перезагрузку не умеет) — переключение на месте.
      if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
        window.location.reload();
        return;
      }
      target.current = locale;
      reload();
    },
    [enabled, reload],
  );

  const requestNamespace = useCallback(
    (namespace: string) => {
      if (wanted.current.has(namespace)) return;
      wanted.current.add(namespace);
      reload();
    },
    [reload],
  );

  // Язык документа (озвучка, переносы, шрифты) и cookie сервера — за
  // показанным языком, а не за запрошенным.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = active.locale;
    storeLocale(active.locale, { explicit: false });
  }, [active.locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale: active.locale,
      locales: enabled,
      setLocale,
      namespaces: active.namespaces,
      requestNamespace,
    }),
    [active.locale, active.namespaces, enabled, setLocale, requestNamespace],
  );

  // Один `intl` на показанный язык: его же видят модули без хуков (`tr`).
  // Публикуется при рендере, а не в эффекте: дети рендерятся раньше эффектов
  // родителя и иначе собрали бы строки на прежнем языке.
  const intl = useMemo(() => {
    const created = decorateIntl(
      createIntl(
        {
          locale: active.locale,
          defaultLocale: SOURCE_LOCALE,
          messages: active.messages as CompleteCatalog,
          onError: onIntlError,
        },
        createIntlCache(),
      ),
    );
    setRuntimeIntl(created);
    return created;
  }, [active.locale, active.messages]);

  // Заголовок вкладки — на языке интерфейса. Экран со своим заголовком
  // (сохранённый отчёт) не перебивается: меняется только заголовок по умолчанию.
  const docTitle = useRef<string | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const next = intl.formatMessage(DOC.title);
    if (docTitle.current === null || document.title === docTitle.current) document.title = next;
    docTitle.current = next;
  }, [intl]);

  return (
    <I18nContext.Provider value={value}>
      <RawIntlProvider value={intl}>
        {/* Смена языка перемонтирует приложение: строки, собранные вне
            React или запомненные в `useMemo` по данным, тоже пересобираются. */}
        <Fragment key={active.locale}>{children}</Fragment>
      </RawIntlProvider>
    </I18nContext.Provider>
  );
}
